import moment from "moment";
import {
  BPMRow,
  DieselPremium,
  Settings,
} from "../../context/SettingsContext/SettingsContext";
import { Car } from "../../services/mobile";

export const getNetCarPrice = (car: Car) => {
  if (car.price === null) {
    throw new Error("No price available");
  }
  if (!car.vatable) {
    return +car.price;
  }

  return +((+car.price / 119) * 100).toFixed(2);
};

export const getStaffelPrice = (
  importPercentageStaffel: Settings["importPercentageStaffel"],
  car: Car
) => {
  let importPercentage = 0;

  const netCarPrice = getNetCarPrice(car);
  importPercentageStaffel.forEach((current) => {
    if (netCarPrice >= current.from && netCarPrice <= current.to) {
      importPercentage = current.percentage;
    }
  });

  return +((netCarPrice / 100) * importPercentage).toFixed(2);
};

export const getLumpPercentage = (
  car: Car,
  lumpPercentageTable: Settings["lumpPercentageTable"]
) => {
  const monthDiff = getRegistrationMonthDiff(
    car,
    moment().get("year"),
    moment().get("month") + 1
  );

  const row = lumpPercentageTable.find(
    (row) => monthDiff >= row.minAge && monthDiff < row.maxAge
  );

  if (!row) {
    throw new Error("No lump percentage found");
  }

  return (
    100 -
    (row.percentage + row.monthlyAdditionPercentage * (monthDiff - row.minAge))
  );
};

export const getImportCosts = (
  importCostsFixed: Settings["importCostsFixed"],
  importPercentageStaffel: Settings["importPercentageStaffel"],
  car: Car
) => {
  const staffelPrice = getStaffelPrice(importPercentageStaffel, car);

  return +(staffelPrice + importCostsFixed).toFixed(2);
};

export const getBpm = (
  bpmRows: BPMRow[] | BPMRow[] | BPMRow[],
  dieselPremiumFrom: DieselPremium["dieselPremiumFrom"],
  dieselPremiumPricePerGram: DieselPremium["dieselPremiumPricePerGram"],
  emission: Car["specifics"]["emission-fuel-consumption"],
  car: Car
) => {
  if (!emission) {
    return 0;
  }
  let dieselPremium = 0;
  if (car.specifics.fuel === "Diesel" && emission > dieselPremiumFrom) {
    dieselPremium = (emission - dieselPremiumFrom) * dieselPremiumPricePerGram;
  }
  return (
    dieselPremium +
    bpmRows.reduce((bpm, bpmRow, index) => {
      if (bpmRow.from === 0) {
        return bpm + bpmRow.pricePerGram;
      }

      if (index === 0) {
        return bpm + Math.min(emission, bpmRow.from) * bpmRow.pricePerGram;
      }
      const previousBpmRow = bpmRows[index - 1];
      if (!previousBpmRow) {
        console.error(car, bpmRows, index);
        throw new Error("No previousBpmRow!");
      }
      if (previousBpmRow.from > emission) {
        return bpm;
      }

      let grams = bpmRow.from;
      if (grams < emission) {
        grams = grams - previousBpmRow.from;
      } else {
        grams = emission - previousBpmRow.from;
      }
      return bpm + grams * bpmRow.pricePerGram;
    }, 0)
  );
};

export const getRegistrationYear = (car: Car) => {
  return moment(car.specifics["first-registration"]).get("year");
};
export const getRegistrationMonthDiff = (
  car: Car,
  fromYear: number,
  fromMonth: number
) => {
  return moment(
    `${fromYear}-${fromMonth < 10 ? `0${fromMonth}` : fromMonth}-01`
  ).diff(car.specifics["first-registration"], "months");
};

export const getBpmRows = (
  fuelBpmMap: {
    [k: string]: BPMRow[];
  },
  fuel: Car["specifics"]["fuel"]
) => {
  if (!fuel) {
    return [];
  }
  return fuelBpmMap[fuel] ? fuelBpmMap[fuel] : [];
};

export const getFuelBpmMap = (
  bpmDiesel: BPMRow[],
  bpmGasoline: BPMRow[],
  bpmHybrid: BPMRow[]
) => {
  return {
    Diesel: bpmDiesel,
    Benzine: bpmGasoline,
    Hybride: bpmHybrid,
    Aardgas: bpmGasoline,
    "Hybride (diesel/elektrisch)": bpmHybrid,
    "Hybride (benzine/elektrisch)": bpmHybrid,
  };
};

export const getVat = (
  importPercentageStaffel: Settings["importPercentageStaffel"],
  car: Car
) => {
  if (!car.vatable) {
    return 0;
  }

  const netCarPrice = getNetCarPrice(car);
  const staffelPrice = getStaffelPrice(importPercentageStaffel, car);

  return +((netCarPrice + staffelPrice) * 0.21).toFixed(2);
};

export const getBPMLumpSum = (
  car: Car,
  allBpmRows: Settings["bpmRows"],
  dieselPremiumFrom: DieselPremium["dieselPremiumFrom"],
  dieselPremiumPricePerGram: DieselPremium["dieselPremiumPricePerGram"],
  lumpPercentageTable: Settings["lumpPercentageTable"]
) => {
  const registrationYear = getRegistrationYear(car);
  const fuelBpmMap = getFuelBpmMap(
    allBpmRows[registrationYear]["bpmDiesel"],
    allBpmRows[registrationYear]["bpmGasoline"],
    allBpmRows[registrationYear]["bpmHybrid"]
  );
  const bpmRows = getBpmRows(fuelBpmMap, car["specifics"]["fuel"]);
  const bpm = getBpm(
    bpmRows,
    dieselPremiumFrom,
    dieselPremiumPricePerGram,
    car["specifics"]["emission-fuel-consumption"],
    car
  );
  const lumpPercentage = getLumpPercentage(car, lumpPercentageTable);

  return +((bpm / 100) * lumpPercentage).toFixed(2);
};

export const getCalculatedPrice = (car: Car, settings: Settings) => {
  const {
    importCostsFixed,
    importPercentageStaffel,
    bpmRows,
    dieselPremium,
    lumpPercentageTable,
  } = settings;
  const registrationYear = getRegistrationYear(car);
  if (!(registrationYear in dieselPremium)) {
    console.error(
      `Registration year ${registrationYear} not available in settings`
    );
    return null;
  }

  const { dieselPremiumFrom, dieselPremiumPricePerGram } = dieselPremium[
    registrationYear
  ];

  let calculatedPrice = getNetCarPrice(car);

  if (!calculatedPrice) {
    console.error("No net car price");
    return null;
  }

  const importCosts = getImportCosts(
    importCostsFixed,
    importPercentageStaffel,
    car
  );

  if (!importCosts) {
    console.error("No import costs");
    return null;
  }

  calculatedPrice += importCosts;

  const vat = getVat(importPercentageStaffel, car);

  calculatedPrice += vat;

  const bpm = getBPMLumpSum(
    car,
    bpmRows,
    dieselPremiumFrom,
    dieselPremiumPricePerGram,
    lumpPercentageTable
  );

  calculatedPrice += bpm;

  return +calculatedPrice.toFixed(2);
};
